import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Card, Divider, Button } from "antd"
import { ReloadOutlined, MailOutlined } from "@ant-design/icons"
import type React from "react"
import EmailFilterForm from "./components/EmailFilterForm"
import EmailLogTable from "./components/EmailLogTable"
import EmailLogDetailModal from "./components/EmailLogDetailModal"
import {
  emailLogService,
  type EmailLogQueryParams,
  type EmailLogItem,
} from "@/services/log/emailLogApi"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"

export const EmailLogPage: React.FC = () => {
  const [searchParams, setSearchParams] = useTableParams<EmailLogQueryParams>({
    ...PAGE_CONFIG,
    search: "",
    email_type: undefined,
    status: undefined,
    start_date: undefined,
    end_date: undefined,
  })

  const [total, setTotal] = useState<number>(0)
  const [selectedRecord, setSelectedRecord] = useState<EmailLogItem | null>(null)

  const {
    data: result,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["email_logs_list", searchParams],
    queryFn: () => {
      return emailLogService.list({
        page: searchParams.page,
        limit: searchParams.per_page,
        search: searchParams.search || undefined,
        email_type: searchParams.email_type || undefined,
        status: searchParams.status || undefined,
        start_date: searchParams.start_date || undefined,
        end_date: searchParams.end_date || undefined,
      })
    },
  })

  const handleSearch = (values: Omit<EmailLogQueryParams, "page" | "limit">) => {
    setSearchParams({
      ...searchParams,
      ...values,
      page: 1,
    })
  }

  const items = result?.data || []
  const totalCount = result?.total ?? total

  return (
    <div className="flex flex-col gap-2">
      <EmailFilterForm
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={{
          search: searchParams.search,
          email_type: searchParams.email_type,
          status: searchParams.status,
          start_date: searchParams.start_date,
          end_date: searchParams.end_date,
        }}
      />

      <Card
        className="flex min-h-0 grow flex-col shadow-xs"
        classNames={{ body: "flex grow flex-col" }}
        title={
          <div className="flex items-center justify-between py-1">
            <div className="flex items-center gap-2">
              <MailOutlined className="text-blue-600 text-lg" />
              <span className="font-semibold text-base text-gray-800">
                Nhật ký gửi Email (Email Logs)
              </span>
              <Divider orientation="vertical" />
              <span className="font-normal text-gray-500 text-sm">
                Tổng số email: <strong className="text-gray-700">{totalCount}</strong>
              </span>
            </div>
            <Button
              icon={<ReloadOutlined spin={isFetching} />}
              onClick={() => refetch()}
              loading={isFetching}
            >
              Làm mới
            </Button>
          </div>
        }
      >
        <EmailLogTable
          data={items}
          loading={isFetching}
          onViewDetail={(record) => setSelectedRecord(record)}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total: totalCount,
            ...PAGINATION_CONFIG,
            onChange(page: number, pageSize: number) {
              setSearchParams({
                ...searchParams,
                page,
                per_page: pageSize,
              })
            },
          }}
        />
      </Card>

      <EmailLogDetailModal
        open={!!selectedRecord}
        record={selectedRecord}
        onClose={() => setSelectedRecord(null)}
      />
    </div>
  )
}

export default EmailLogPage
